import * as system from 'durandal/system';
import * as ko from 'knockout';

import { Comparison, PaginationFilters, SearchPaginationOptions } from '@/features/api';
import { SortOrder } from '@/features/api/types/pagination';
// FIXME: Long-tail import avoids circular dependency
import { UploadedFileViewModel } from '@/features/file-attachments/types/UploadedFileViewModel';
import { CreditCardPayableStatus } from '@/features/pay-by-card/types';
import { SortableFieldTypes } from '@/legacy/features/companies/payees/interfaces';
import PayeeRestApiService from '@/legacy/features/companies/payees/payeesRestApiService';
import * as companyService from '@/legacy/services/company';
import * as userService from '@/legacy/services/user';
import * as plootoUtils from '@/legacy/utils/plooto';
import * as addModels from '@/legacy/viewmodels/addModels';
import * as viewModels from '@/legacy/viewmodels/viewModels';
import { PaymentDestination } from '@/legacy/viewmodels/viewModels';

const API_PREFIX = 'v1';

export enum PayeeBusinessType {
  /**
   * A vendor is who you purchase from (Payables)
   **/
  Vendor = 'Vendor',

  /**
   * A customer is who you sell to (Receivables)
   **/
  Customer = 'Customer',
}

export enum PAY_TO_ITEM_TYPE {
  PAYEE,
  ACCOUNT,
}
export class PayToItem {
  payeeName: KnockoutObservable<string>;

  payeeId: KnockoutObservable<string>;

  payeeAccountId: KnockoutObservable<string>;

  name: KnockoutObservable<string>;

  accounts: KnockoutObservableArray<PayeeAccount>;

  type: PAY_TO_ITEM_TYPE;
}

export interface PayeeDefaultDestinationRequest {
  uniqueIdentifier: string;
  payeeId: string;
  currency: string;
}

export interface PayeeDefaultDestinationResponse {
  uniqueIdentifier: string;
  destination: PaymentDestination;
}

export interface GetAvailablePaymentMethodsParams {
  country: string;
  skipRequestByEmail: boolean;
  displayPlootoNetworkOption: boolean;
}

export interface IAvailableRequestFromItem {
  text: string;
  value: string;
}

export class PayeeAttachmentDetails {
  public name: string;

  public contentType: string;

  public url: string;
}

export class PayeePadStatusViewModel {
  public padStatus: string;

  public padAcceptedDate: string;

  public pinSentDate: string;
}
export class PayeeAccountAddBindingModel {
  public account: addModels.PayeeAccountBindingModel;

  // These will show up for iACH/Wire payments
  public country: string;

  public address: string;

  public suite: string;

  public state: string;

  public city: string;

  public zipCode: string;

  public phone: string;

  public locale: string;

  public isOptionToPayByCardEnabled: boolean;

  public industry: string;

  public industrySubCategory: string;
}
export class PayeeAccount {
  /*
  @todo bankInformationFields.html will use this object to store some additional information
  (check out value: account.transit/.instituion/.account)
  These properties are not actually defined in this class. We need to stop assigning those directly to PayeeAccount and
  use a proper object where those are defined.
  */
  public id = ko.observable<string>('');

  public name = ko.observable<string>('');

  public bank = ko.observable<string>('');

  public country = ko.observable<string>('');

  public creditFailed = ko.observable<boolean>(false);

  public currency = ko.observable<string>('');

  public emailMoneyTransfer = ko.observable<boolean>(false);

  public bankDetailsRequired = ko.observable<boolean>(false);

  public containsAccountInformation = ko.observable<boolean>(false);

  public securityQuestion = ko.observable<string>(''); // Email Money Transfer question

  public securityAnswer = ko.observable<string>(''); // Email Money Transfer answer

  public deleted = ko.observable<boolean>(false);

  public payeeId = ko.observable<string>('');

  public payee = ko.observable<Payee>(undefined);

  public accountDetails = ko.observable<viewModels.KOBankAccountDetailsViewModel>(null);

  public displayName = ko.observable<string>('');

  public accountTransactionType = ko.observable<string>('');

  public discriminator = ko.observable<string>('');

  public checkAddress = ko.observable<string>('');

  public checkSuite = ko.observable<string>('');

  public checkState = ko.observable<string>('');

  public checkCity = ko.observable<string>('');

  public checkZipCode = ko.observable<string>('');

  public checkPrintNameOnCheckAs = ko.observable<string>('');

  public additionalVisibleBankFields = ko.observableArray<string>([]);

  constructor(payee: Payee, info: any) {
    this.payee(payee);
    this.Update(info);
  }

  public Update(accountInfo) {
    for (const property in accountInfo) {
      // if updated data doesnt specify property skip
      if (accountInfo[property] !== undefined) {
        const newValue = ko.utils.unwrapObservable(accountInfo[property]);

        if (property == 'accountDetails') {
          this.accountDetails(new viewModels.KOBankAccountDetailsViewModel(newValue));
        } else {
          const shouldSkip =
            $.isArray(newValue) ||
            $.isPlainObject(newValue) ||
            ko.isComputed(this[property]) ||
            !ko.isObservable(this[property]);

          if (!shouldSkip) {
            if (this[property] === undefined) {
              throw new Error(`Property "${property}" not found`);
            } else {
              // set value
              this[property](newValue);
            }
          }
        }
      }
    }
  }
}
export class Payee {
  public id = ko.observable<string>('');

  public name = ko.observable<string>('');

  public companyId = ko.observable<string>('');

  public address = ko.observable<string>('');

  public city = ko.observable<string>('');

  public country = ko.observable<string>('');

  public phone = ko.observable<string>('');

  public contact = ko.observable<string>('');

  public email = ko.observable<string>('');

  public state = ko.observable<string>('');

  public stateName = ko.observable<string>('');

  public suite = ko.observable<string>('');

  public zipCode = ko.observable<string>('');

  public locale = ko.observable<string>('');

  public type = ko.observable<string>('Contact');

  public deleted = ko.observable<boolean>(false);

  public padStatus = ko.observable<string>('');

  public registrationDate = ko.observable<Date>(new Date());

  public universalPayeeId = ko.observable<string>(undefined); // for universal payees

  public currency = ko.observable<string>(null); // for universal payees

  public accounts = ko.observableArray<PayeeAccount>([]);

  public displayName = ko.observable<string>('');

  public vendorAccountNumber = ko.observable<string>('');

  public printNameOnCheckAs = ko.observable<string>('');

  public isNetworkPayee = ko.observable<boolean>(false);

  public universalPayee = ko.observable<any>('');

  public businessType = ko.observable<string>('');

  public accountingSoftwarePayeeId = ko.observable<string>('');

  public accountingSoftwarePayeeName = ko.observable<string>('');

  public industry = ko.observable<string>(null);

  public industrySubCategory = ko.observable<string>(null);

  public creditCardPayableStatus = ko.observable<CreditCardPayableStatus>(null);

  public uploadedFiles: Array<UploadedFileViewModel> = [];

  public isEditable: KnockoutComputed<boolean> = ko.computed({
    read: () => {
      // Allow network payees to be editable (even though they're universal payees)
      if (this.isNetworkPayee()) {
        return true;
      }

      return !this.universalPayeeId();
    },
    deferEvaluation: true,
  });

  public isDeletable: KnockoutComputed<boolean> = ko.computed({
    read: () => this.type() == 'Contact',
    deferEvaluation: true,
  });

  constructor(info: any) {
    this.Update(info);
  }

  public Update(accountInfo) {
    for (const property in accountInfo) {
      // if updated data doesnt specify property skip
      if (accountInfo[property] !== undefined) {
        const newValue = ko.utils.unwrapObservable(accountInfo[property]);

        switch (property) {
          // Some objects like universalPayee are complex and we want to preserve their structure in a basic observable
          case 'universalPayee':
            this.universalPayee(newValue);
            break;

          case 'paymentsLoaderStatus':
            break;

          default: {
            const shouldSkip =
              $.isArray(newValue) ||
              $.isPlainObject(newValue) ||
              ko.isComputed(this[property]) ||
              !ko.isObservable(this[property]);

            if (!shouldSkip) {
              this[property](newValue);
            }

            break;
          }
        }
      }
    }

    if (accountInfo && accountInfo.accounts !== undefined) {
      const accounts = ko.utils.unwrapObservable(accountInfo.accounts);

      const payeeAccounts = [];
      $.each(accounts, (accountIndex, account) => {
        payeeAccounts.push(new PayeeAccount(this, account));
      });
      this.accounts(payeeAccounts);
    }
  }
}

export class PayeeService {
  private loaderStatus: KnockoutObservable<string> = ko
    .observable(undefined)
    .extend({ notify: 'always', rateLimit: { timeout: 50, method: 'notifyWhenChangesStop' } });

  private membershipSubscription: KnockoutSubscription = undefined;

  private companyService: companyService.CompanyService;

  private userService: userService.UserService;

  constructor() {
    this.companyService = companyService.Instance;
    this.userService = userService.Instance;
  }

  public CreatePayee(payeeImportModel: addModels.PayeeImportBindingModel): JQueryPromise<Payee> {
    const companyId: string = this.companyService.CompanyId();

    return system
      .defer<Payee>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedPut(
            `${import.meta.env.APP_URLS_API_URL}/${API_PREFIX}/companies/${companyId}/payees`,
            payeeImportModel
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }
            const payee = new Payee(accountInfo);

            dfd.resolve(payee);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public GetPayeeById(payeeId: string): JQueryPromise<Payee> {
    const companyId: string = this.companyService.CompanyId();

    return system
      .defer<Payee>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedGet(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${companyId}/payees/${payeeId}`
          )
          .done((payeeInfo) => {
            if (plootoUtils.IsNullOrUndefined(payeeInfo) || payeeInfo.error === true) {
              dfd.reject(payeeInfo);
              return;
            }
            const payee = new Payee(payeeInfo);

            dfd.resolve(payee);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public getOrCreateUniversalPayeeById(universalPayeeId: string): JQueryPromise<Payee> {
    const companyId: string = this.companyService.CompanyId();

    return system
      .defer<Payee>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedGet(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${companyId}/payees/universalPayee/${universalPayeeId}`
          )
          .done((payeeInfo) => {
            if (plootoUtils.IsNullOrUndefined(payeeInfo) || payeeInfo.error === true) {
              dfd.reject(payeeInfo);
              return;
            }
            const payee = new Payee(payeeInfo);

            dfd.resolve(payee);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public getPayeesDefaultDestinations(
    payeeDestinationRequest: Array<PayeeDefaultDestinationRequest>
  ): JQueryPromise<Array<PayeeDefaultDestinationResponse>> {
    const companyId: string = this.companyService.CompanyId();

    return system
      .defer<Array<PayeeDefaultDestinationResponse>>((dfd) => {
        this.userService
          .AuthorizedPost(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${companyId}/payees/defaultDestinationAccounts`,
            payeeDestinationRequest
          )
          .done((response) => {
            if (plootoUtils.IsNullOrUndefined(response) || response.error === true) {
              dfd.reject(response);
              return;
            }

            dfd.resolve(response);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public RemovePayee(payeeId: string): JQueryPromise<void> {
    return system
      .defer<void>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedDelete(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}`
          )
          .done((confirmation) => {
            if (plootoUtils.IsNullOrUndefined(confirmation) || confirmation.error === true) {
              dfd.reject(confirmation);
              return;
            }
            dfd.resolve();
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public RemovePayeeAccount(
    payeeId: string,
    accountId: string,
    cancelPad: boolean
  ): JQueryPromise<void> {
    return system
      .defer<void>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedDelete(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}/accounts/${accountId}`,
            { cancelPad }
          )
          .done((confirmation) => {
            if (plootoUtils.IsNullOrUndefined(confirmation) || confirmation.error === true) {
              dfd.reject(confirmation);
              return;
            }

            dfd.resolve();
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public RenamePayeeAccount(payeeId: string, accountId: string, name: string): JQueryPromise<void> {
    return system
      .defer<void>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedPost(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}/accounts/${accountId}`,
            {
              name,
            }
          )
          .done((newInfo) => {
            if (plootoUtils.IsNullOrUndefined(newInfo) || newInfo.error === true) {
              dfd.reject(newInfo);
              return;
            }

            dfd.resolve();
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public PostCancelPadStatus(payeeId: string): JQueryPromise<void> {
    return system
      .defer<void>((dfd) => {
        this.userService
          .AuthorizedPost(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}/cancelPadStatus`
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }
            dfd.resolve(accountInfo);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public getPadStatus(payeeId: string): JQueryPromise<PayeePadStatusViewModel> {
    return system
      .defer<PayeePadStatusViewModel>((dfd) => {
        this.userService
          .AuthorizedGet(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}/padStatus`
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }
            dfd.resolve(accountInfo);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public GetRequiredFields(
    country: string,
    currency: string
  ): JQueryPromise<viewModels.PayeeAccountRequiredFieldsViewModel> {
    return system
      .defer<viewModels.PayeeAccountRequiredFieldsViewModel>((dfd) => {
        this.userService
          .AuthorizedGetFromUri(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/requiredFields`,
            {
              country,
              currency,
            }
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }
            dfd.resolve(accountInfo);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public ImportPayees(
    payees: Array<addModels.PayeeImportBindingModel>
  ): JQueryPromise<Array<Payee>> {
    const companyId: string = this.companyService.CompanyId();
    return system
      .defer<Array<Payee>>((dfd) => {
        this.userService
          .AuthorizedPut(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${companyId}/payees/import`,
            payees
          )
          .done((accountsData) => {
            if (plootoUtils.IsNullOrUndefined(accountsData) || accountsData.error === true) {
              dfd.reject(accountsData);
              return;
            }

            const results: Array<Payee> = [];

            for (let i = 0; i < accountsData.length; i += 1) {
              results.push(new Payee(accountsData[i]));
            }

            dfd.resolve(results);
          })
          .fail((err) => {
            dfd.reject(err);
          });
      })
      .promise();
  }

  public AddPayeeAccount(
    payeeId: string,
    newAccount: PayeeAccountAddBindingModel
  ): JQueryPromise<viewModels.PayeeAccountViewModel> {
    return system
      .defer<viewModels.PayeeAccountViewModel>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedPut(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}/accounts`,
            newAccount
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }

            dfd.resolve(accountInfo);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public UpdatePayee(payeeId: string, payeeImportModel): JQueryPromise<Payee> {
    return system
      .defer<Payee>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedPost(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}`,
            payeeImportModel
          )
          .done((payeeInfo) => {
            if (plootoUtils.IsNullOrUndefined(payeeInfo) || payeeInfo.error === true) {
              dfd.reject(payeeInfo);
              return;
            }

            const payee = new Payee(payeeInfo);

            dfd.resolve(payee);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public UpdatePayeeAccount(
    payeeId: string,
    payeeAccountId: string,
    payeeImportModel
  ): JQueryPromise<Payee> {
    return system
      .defer<Payee>((dfd) => {
        // TODO: Error handling
        this.userService
          .AuthorizedPost(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}/accounts/${payeeAccountId}`,
            payeeImportModel
          )
          .done((accountInfo) => {
            if (plootoUtils.IsNullOrUndefined(accountInfo) || accountInfo.error === true) {
              dfd.reject(accountInfo);
              return;
            }
            dfd.resolve(accountInfo);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }

  public getPayeeJsonById(payeeId: string, ignoreCache?: boolean): JQueryPromise<any> {
    const companyId: string = this.companyService.CompanyId();
    return system
      .defer<any>((dfd) => {
        this.userService
          .AuthorizedGet(
            `${
              import.meta.env.APP_URLS_API_URL
            }/${API_PREFIX}/companies/${companyId}/payees/${payeeId}`
          )
          .then((payeeInfo) => {
            if (plootoUtils.IsNullOrUndefined(payeeInfo) || payeeInfo.error === true) {
              dfd.reject(payeeInfo);
              return;
            }

            dfd.resolve(payeeInfo);
          })
          .fail((error) => {
            dfd.reject(error);
          });
      })
      .promise();
  }

  public async getCompanyContactsBySearchQueryWithXHR(
    searchQuery: string,
    excludeUniversalPayees?: boolean
  ) {
    const companyId: string = this.companyService.CompanyId();
    if (companyId === undefined) {
      return undefined;
    }

    const filters: Array<PaginationFilters> = [
      {
        type: 'Type',
        comparison: Comparison.Equals,
        value: 'CompanyContact',
      },
    ];

    if (excludeUniversalPayees) {
      filters.push({
        type: 'ExcludeUniversalPayee',
        value: excludeUniversalPayees.toString(),
      });
    }

    const response = await PayeeRestApiService.getPayees(companyId, {
      filters,
      resultsPerPage: 50,
      searchQuery: $.trim(searchQuery),
      sortDetails: [
        {
          type: SortableFieldTypes.Name,
          order: SortOrder.Ascending,
        },
      ],
    });
    return response.items as unknown as Array<viewModels.PayeeViewModel>;
  }

  public async getCompanyContactsBySearchQuery(
    companyId: string,
    searchQuery: string,
    excludeUniversalPayees?: boolean
  ): Promise<Array<viewModels.PayeeViewModel>> {
    const options = {
      searchQuery,
      resultsPerPage: 50,
      filters: [
        {
          type: 'excludeUniversalPayee',
          value: excludeUniversalPayees ?? 'false',
        },
      ],
      sortDetails: [
        {
          type: SortableFieldTypes.Name,
          order: SortOrder.Ascending,
        },
      ],
    } as SearchPaginationOptions;

    const response = await PayeeRestApiService.getPayees(companyId, options);
    return response.items.map((item) => item as unknown as viewModels.PayeeViewModel);
  }

  public getAvailablePaymentMethods({
    country,
    skipRequestByEmail,
    displayPlootoNetworkOption,
  }: GetAvailablePaymentMethodsParams): any {
    const paymentMethods = [];

    if (!skipRequestByEmail) {
      paymentMethods.push({ text: 'Select', value: '' });
      if (displayPlootoNetworkOption) {
        paymentMethods.push({ text: 'Add to Plooto Network (Canada Only)', value: 'NetworkPayee' });
      }
    } else {
      paymentMethods.push({ text: 'Select', value: '' });
      if (displayPlootoNetworkOption) {
        paymentMethods.push({ text: 'Add to Plooto Network (Canada Only)', value: 'NetworkPayee' });
      }
    }
    paymentMethods.push({ text: 'Email Money Transfer', value: 'EmailMoneyTransfer' });
    paymentMethods.push({ text: 'Enter Bank Account Information', value: 'BankAccount' });

    switch (country) {
      case 'CA':
      case 'US':
        paymentMethods.push({ text: 'Mailed Check (U.S. Payees Only)', value: 'PhysicalCheck' });
        break;
    }

    return paymentMethods;
  }

  public GetAttachmentsForPayeeId(payeeId: string): JQueryPromise<Array<PayeeAttachmentDetails>> {
    return system
      .defer<Array<PayeeAttachmentDetails>>((dfd) => {
        userService.Instance.AuthorizedGet(
          `${
            import.meta.env.APP_URLS_API_URL
          }/${API_PREFIX}/companies/${this.companyService.CompanyId()}/payees/${payeeId}/attachments`
        )
          .then((attachments) => {
            if (plootoUtils.IsNullOrUndefined(attachments) || attachments.error === true) {
              dfd.reject(attachments);
              return;
            }

            dfd.resolve(attachments);
          })
          .fail(() => {
            dfd.reject();
          });
      })
      .promise();
  }
}
export const Instance = new PayeeService();
